import axiosInstance from "@/api/axiosInstance";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import { ENDPOINTS } from "@/constants/api";
import { Competition } from "@/common/model/Competition";
import { Button, Input, Card } from "@/components/ui";
import CreateCompetitionDialog from "./CreateCompetitionDialog";
import DeleteCompetitionDialog from "./DeleteCompetitionDialog";

// Define sort options
type SortOption = {
  label: string;
  value: string;
};

const sortOptions: SortOption[] = [
  { label: "Latest Date First", value: "latest" },
  { label: "Oldest Date First", value: "oldest" },
  { label: "Name A-Z", value: "nameAsc" },
  { label: "Name Z-A", value: "nameDesc" },
  { label: "Location A-Z", value: "locationAsc" },
];

const CompetitionList: React.FC = () => {
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<string>("latest"); // Default sort by latest date
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState<Competition | null>(null);
  const navigate = useNavigate();

  const fetchCompetitions = () => {
    setLoading(true);
    axiosInstance
      .get(`${ENDPOINTS.COMPETITIONS.DEFAULT}/event`)
      .then((response) => {
        setCompetitions(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching competitions:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    // Fetch competitions from the backend
    fetchCompetitions();
  }, []);

  // Filter competitions based on search term
  const filteredCompetitions = competitions.filter((competition) => {
    // If no search term, show all competitions
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Check each field safely
    return (
      (competition.name && competition.name.toLowerCase().includes(searchLower)) ||
      (competition.location && competition.location.toLowerCase().includes(searchLower)) ||
      (competition.description && competition.description.toLowerCase().includes(searchLower))
    );
  });

  // Helper function to safely parse date
  const parseDate = (dateValue?: string | number | null): Date => {
    if (!dateValue) return new Date(0); // Return epoch date for null/undefined/empty strings

    // Handle numeric timestamps (milliseconds)
    if (typeof dateValue === 'number') {
      return new Date(dateValue);
    }

    // Handle UTC timezone indicator
    if (typeof dateValue === 'string' && dateValue.includes('UTC')) {
      return new Date(dateValue.replace('[UTC]', ''));
    }
    return new Date(dateValue);
  };

  // Sort competitions based on selected sort option
  const sortedCompetitions = [...filteredCompetitions].sort((a, b) => {
    switch (sortBy) {
      case "latest":
        return parseDate(b.startDate).getTime() - parseDate(a.startDate).getTime();
      case "oldest":
        return parseDate(a.startDate).getTime() - parseDate(b.startDate).getTime();
      case "nameAsc":
        return (a.name || "").localeCompare(b.name || "");
      case "nameDesc":
        return (b.name || "").localeCompare(a.name || "");
      case "locationAsc":
        return (a.location || "").localeCompare(b.location || "");
      default:
        // Default to latest first
        return parseDate(b.startDate).getTime() - parseDate(a.startDate).getTime();
    }
  });

  const handleCompetitionSelect = (competition: Competition) => {
    // Store the competition ID and name in session storage
    sessionStorage.setItem("competitionId", competition.id.toString());
    sessionStorage.setItem("competitionName", competition.name || "");
    // Navigate to the competition details page
    navigate(`/competitions/details?id=${competition.id}`);
  };

  const handleDeleteClick = (competition: Competition, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation when clicking delete
    setSelectedCompetition(competition);
    setIsDeleteDialogOpen(true);
  };

  const handleCompetitionCreated = (newCompetition: Competition) => {
    setIsCreateDialogOpen(false);
    // Refresh the competitions list
    fetchCompetitions();
  };

  // Function to format date with UTC handling
  const formatDate = (dateValue: string | number | undefined | null) => {
    if (!dateValue) {
      return "N/A"; // Return a default value if dateValue is undefined or null
    }

    // Handle numeric timestamps (milliseconds)
    if (typeof dateValue === 'number') {
      return moment(dateValue).format("DD-MM-YYYY");
    }

    // Handle dates with UTC timezone indicator
    if (typeof dateValue === 'string' && dateValue.includes('UTC')) {
      return moment(dateValue.replace('[UTC]', '')).format("DD-MM-YYYY");
    }
    return moment(dateValue).format("DD-MM-YYYY");
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Competitions</h1>
        <Button 
          onClick={() => setIsCreateDialogOpen(true)}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          Create Competition
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div className="flex-1">
          <Input
            type="text"
            placeholder="Search competitions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
        <div className="w-full md:w-64">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading competitions...</div>
      ) : sortedCompetitions.length === 0 ? (
        <div className="text-center py-8">
          {searchTerm ? "No competitions match your search" : "No competitions found"}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedCompetitions.map((competition) => (
            <div 
              key={competition.id} 
              className="group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
            >
              {/* Colored accent bar at top */}
              <div className="h-2 bg-gradient-to-r from-blue-500 to-indigo-600"></div>

              <div className="p-6">
                {/* Header with name and badges */}
                <div className="mb-4">
                  <h2 className="text-xl font-bold text-gray-800 mb-1 group-hover:text-blue-600 transition-colors line-clamp-2">
                    {competition.name || "Unnamed Competition"}
                  </h2>

                  <div className="flex flex-wrap gap-2 mt-2">
                    {competition.location && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {competition.location}
                      </span>
                    )}

                    {/* Only show date badge if at least one date exists */}
                    {(competition.startDate || competition.endDate) && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formatDate(competition.startDate)} - {formatDate(competition.endDate)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Description with truncation */}
                {competition.description && (
                  <div className="mb-4">
                    <p className="text-gray-600 text-sm line-clamp-3">
                      {competition.description}
                    </p>
                  </div>
                )}

                {/* Stats section */}
                <div className="flex gap-4 mb-5 mt-4">
                  <div className="flex-1 bg-blue-50 rounded-lg p-3 text-center border border-blue-100">
                    <span className="block text-xl font-bold text-blue-700">
                      {competition.heatList?.length || 0}
                    </span>
                    <span className="text-xs text-blue-600 font-medium">Heats</span>
                  </div>

                  <div className="flex-1 bg-green-50 rounded-lg p-3 text-center border border-green-100">
                    <span className="block text-xl font-bold text-green-700">
                      {competition.eventusers?.length || 0}
                    </span>
                    <span className="text-xs text-green-600 font-medium">Users</span>
                  </div>
                </div>
              </div>

              {/* Action buttons with gradient overlay to ensure text readability */}
              <div className="p-4 border-t border-gray-100 bg-gradient-to-b from-white to-gray-50">
                <div className="flex gap-2">
                  <Button 
                    onClick={() => handleCompetitionSelect(competition)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-1"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View Details
                  </Button>
                  <Button 
                    variant="delete"
                    onClick={(e) => handleDeleteClick(competition, e)}
                    className="px-3 flex items-center justify-center"
                    aria-label="Delete competition"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateCompetitionDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={handleCompetitionCreated}
      />

      <DeleteCompetitionDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        competition={selectedCompetition}
        onSuccess={fetchCompetitions}
      />
    </div>
  );
};

export default CompetitionList;
